import { isPreviewMode } from '@/assetSource'
import { useAuthStore } from '@/stores/auth'
import type { AppcuesUserProperties } from './types'

const APPCUES_ACCOUNT_ID = import.meta.env.VITE_APPCUES_ACCOUNT_ID
const isDevelopment = import.meta.env.DEV
const isTest = import.meta.env.MODE === 'test'

let hasInitialized = false

class AppcuesService {
  private unsubscribe?: () => void

  get skipRequest() {
    return isDevelopment || isTest || isPreviewMode()
  }

  init() {
    if (hasInitialized || this.skipRequest) return

    if (!APPCUES_ACCOUNT_ID) {
      console.warn('Appcues: VITE_APPCUES_ACCOUNT_ID not configured')
      return
    }

    if (!window.Appcues) {
      console.error('Appcues: Script not loaded')
      return
    }

    hasInitialized = true
    this.setupUserSubscription()
  }

  identify(userId: string, properties: AppcuesUserProperties = {}) {
    if (this.skipRequest || !window.Appcues) return

    window.Appcues.identify(userId, properties)
  }

  track(eventName: string, properties: Record<string, unknown> = {}) {
    if (this.skipRequest || !window.Appcues) return

    try {
      window.Appcues.track(eventName, properties)
    } catch (error) {
      console.warn(`[Appcues] Failed to track "${eventName}":`, error)
    }
  }

  page() {
    if (this.skipRequest || !window.Appcues) return

    window.Appcues.page()
  }

  group(groupId: string, properties: Record<string, unknown> = {}) {
    if (this.skipRequest || !window.Appcues) return

    window.Appcues.group(groupId, properties)
  }

  reset() {
    if (this.skipRequest || !window.Appcues) return

    window.Appcues.reset()
  }

  show(flowId: string) {
    if (this.skipRequest || !window.Appcues) return

    window.Appcues.show(flowId)
  }

  destroy() {
    this.unsubscribe?.()
    this.unsubscribe = undefined
    hasInitialized = false
  }

  private setupUserSubscription() {
    const { user, organization, role } = useAuthStore.getState()

    if (user) {
      this.identifyUser(user, organization, role)
    }

    this.unsubscribe = useAuthStore.subscribe(
      ({ user, organization, role }) => {
        if (user) {
          this.identifyUser(user, organization, role)
        } else {
          this.reset()
        }
      }
    )
  }

  private identifyUser(
    user: NonNullable<ReturnType<typeof useAuthStore.getState>['user']>,
    organization: ReturnType<typeof useAuthStore.getState>['organization'],
    role: ReturnType<typeof useAuthStore.getState>['role']
  ) {
    // Privacy Note: We intentionally avoid sending PII (email, names) to Appcues.
    // Only the user ID and non-PII attributes are shared for onboarding segmentation.
    // Ensure your privacy policy and DPA with Appcues covers this data sharing.
    this.identify(user.id, {
      organizationId: organization?.id,
      organizationName: organization?.name,
      role: role?.name,
      createdAt: user.createdAt,
    })

    if (organization) {
      this.group(organization.id, {
        name: organization.name,
        domain: organization.domain,
      })
    }
  }
}

// Singleton instance for app-wide usage
export const appcuesService = new AppcuesService()

// Export class for testing purposes
export { AppcuesService }
